/**
 * Analytics Hook
 * Presentation layer - React hook for easy event tracking
 * Uses environment variables for configuration
 */

import { useEffect, useMemo, useState } from 'react';
import type { AnalyticsService } from '../../application/services/AnalyticsService';
import { createAnalytics } from '../../config/analytics.build';
import { setStoreReference } from '../../utils/commonAttributes';
import { useAppStore } from '@/app/store/store';

/**
 * Analytics Hook Result
 */
export interface UseAnalyticsResult {
  /**
   * Analytics service instance
   */
  analytics: AnalyticsService | null;
  
  /**
   * Whether analytics is initialized
   */
  isInitialized: boolean;
  
  /**
   * Error if initialization failed
   */
  error: Error | null;
}

/**
 * Use Analytics Hook
 * Provides analytics service for tracking events
 * Configuration is loaded from environment variables
 * 
 * @returns Analytics service and initialization state
 * 
 * @example
 * ```tsx
 * const { analytics } = useAnalytics();
 * 
 * const handleClick = () => {
 *   analytics?.trackEvent('button_click', { section: 'header' });
 * };
 * ```
 */
export function useAnalytics(): UseAnalyticsResult {
  const store = useAppStore();
  const [analytics, setAnalytics] = useState<AnalyticsService | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Set store reference for user info access from Redux
    setStoreReference(store);
  }, [store]);

  useEffect(() => {
    let isMounted = true;

    const initializeAnalytics = async () => {
      try {
        const service = await createAnalytics();
        
        if (isMounted) {
          setAnalytics(service);
          setIsInitialized(true);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('Failed to initialize analytics'));
          setIsInitialized(false);
        }
      }
    };

    void initializeAnalytics();

    return () => {
      isMounted = false;
    };
  }, []);

  return useMemo(
    () => ({
      analytics,
      isInitialized,
      error,
    }),
    [analytics, isInitialized, error]
  );
}
